import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Package, Undo2, Plus, Inbox, Trash2, Laptop } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const ISSUANCE_STATUS_META = {
  issued: { label: "Issued", chip: "bg-sky-100 text-sky-800" },
  return_requested: { label: "Return Requested", chip: "bg-amber-100 text-amber-800" },
  returned: { label: "Returned", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", chip: "bg-rose-100 text-rose-800" },
};
const REQ_STATUS_META = {
  pending: { label: "Pending", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", chip: "bg-rose-100 text-rose-800" },
};
const PRIORITY_CHIP = {
  Urgent: "bg-rose-100 text-rose-800",
  High: "bg-amber-100 text-amber-800",
  Normal: "bg-sky-100 text-sky-800",
  Low: "bg-slate-100 text-slate-700",
};
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

export default function MyAssets({ actionPulse = null } = {}) {
  const [viewMode] = useGlobalViewMode();
  const [assets, setAssets] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [assetRequests, setAssetRequests] = useState([]);
  const [issuanceRequests, setIssuanceRequests] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [returnTarget, setReturnTarget] = useState(null);
  const [assetReturnTarget, setAssetReturnTarget] = useState(null);
  const [returnRemark, setReturnRemark] = useState("");

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestType, setRequestType] = useState(""); // "it_asset" | "product"
  const [assetReqForm, setAssetReqForm] = useState({ product_name: "", cost: "", remark: "", priority: "Normal", required_by: "" });
  const [issReqForm, setIssReqForm] = useState({ items: [{ inventory_id: "", qty: 1 }], reason: "", project_name: "", priority: "Normal", required_by: "" });
  const [saving, setSaving] = useState(false);

  const loadAssets = () => api.get("/my-assets").then(({ data }) => setAssets(data || [])).catch(() => setAssets([]));
  const loadIssuances = () => api.get("/my-product-issuances").then(({ data }) => setIssuances(data || [])).catch(() => setIssuances([]));
  const loadAssetRequests = () => api.get("/asset-requests?scope=mine").then(({ data }) => setAssetRequests(data || [])).catch(() => setAssetRequests([]));
  const loadIssuanceRequests = () => api.get("/issuance-requests?scope=mine").then(({ data }) => setIssuanceRequests(data || [])).catch(() => setIssuanceRequests([]));

  useEffect(() => {
    loadAssets();
    loadIssuances();
    loadAssetRequests();
    loadIssuanceRequests();
    api.get("/inventory/items").then(({ data }) => setInventory(data || [])).catch(() => {});
  }, []);

  // v109.9.55 — Dashboard "Request Asset" quick-action deep-link.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("action") === "request") {
      setRequestType("");
      setRequestOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
     
  }, [searchParams]);

  // v109.9.56 — also support prop-based actionPulse (opened as modal from Dashboard).
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") {
      setRequestType("");
      setRequestOpen(true);
    }
  }, [actionPulse]);

  // Return material (existing)
  const submitReturn = async () => {
    if (!returnTarget) return;
    setSaving(true);
    try {
      await api.post(`/product-issuances/${returnTarget.id}/request-return`, { remark: returnRemark });
      toast.success("Return request sent to IT Admin");
      setReturnTarget(null); setReturnRemark("");
      loadIssuances();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };

  // Return asset
  const submitAssetReturn = async () => {
    if (!assetReturnTarget) return;
    setSaving(true);
    try {
      await api.post(`/it-assets/${assetReturnTarget.id}/request-return`, { remark: returnRemark });
      toast.success("Return request sent to IT Admin");
      setAssetReturnTarget(null); setReturnRemark("");
      loadAssets();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };

  // New request flow
  const openRequest = () => { setRequestType(""); setRequestOpen(true); };
  const resetForms = () => {
    setAssetReqForm({ product_name: "", cost: "", remark: "", priority: "Normal", required_by: "" });
    setIssReqForm({ items: [{ inventory_id: "", qty: 1 }], reason: "", project_name: "", priority: "Normal", required_by: "" });
  };

  const submitAssetRequest = async () => {
    if (!assetReqForm.product_name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      await api.post("/asset-requests", {
        product_name: assetReqForm.product_name,
        cost: Number(assetReqForm.cost) || 0,
        remark: assetReqForm.remark,
        priority: assetReqForm.priority,
        required_by: assetReqForm.required_by,
      });
      toast.success("Asset request submitted");
      setRequestOpen(false); resetForms();
      loadAssetRequests();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };

  const submitIssuanceRequest = async () => {
    const items = issReqForm.items.filter((i) => i.inventory_id && Number(i.qty) > 0);
    if (items.length === 0) { toast.error("Add at least one product with qty > 0"); return; }
    if (!issReqForm.reason.trim()) { toast.error("Why is the product required?"); return; }
    setSaving(true);
    try {
      await api.post("/issuance-requests", {
        items: items.map((i) => ({ inventory_id: i.inventory_id, qty: Number(i.qty) })),
        reason: issReqForm.reason,
        project_name: issReqForm.project_name,
        priority: issReqForm.priority,
        required_by: issReqForm.required_by,
      });
      toast.success("Material request submitted");
      setRequestOpen(false); resetForms();
      loadIssuanceRequests();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };

  const totalDeducted = useMemo(() => {
    const fromIssuances = issuances
      .filter((i) => i.status === "returned")
      .reduce((sum, i) => sum + (i.items || []).filter((it) => it.deduct_cost).reduce((s, it) => s + Number(it.deduct_amount || 0), 0), 0);
    const fromAssets = assets
      .filter((a) => a.return_status === "returned" && a.return_deduct_cost)
      .reduce((s, a) => s + Number(a.return_deduct_amount || 0), 0);
    return fromIssuances + fromAssets;
  }, [issuances, assets]);

  return (
    <div className="max-w-[1300px] space-y-6">
      <PageHeader
        moduleKey="it_assets"
        tone="sky"
        icon={Laptop}
        eyebrow="Workspace"
        title="My Assets"
        subtitle="Assets & materials issued to you, plus any cost deductions and your pending requests."
        actions={
          <Button onClick={openRequest} className="bg-gradient-to-br from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white h-10 shadow-sm" data-testid="my-assets-raise-request">
            <Plus size={14} className="mr-1.5" /> Raise Request
          </Button>
        }
      />

      {totalDeducted > 0 && (
        <div className="pk-card p-4 bg-rose-50 border-rose-200" data-testid="my-assets-deduction-banner">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-rose-700">Total Cost Deduction (returns)</div>
          <div className="mono text-2xl font-bold text-rose-800 mt-1">₹ {totalDeducted.toLocaleString("en-IN")}</div>
        </div>
      )}

      {/* IT-tagged Assets */}
      <section data-testid="my-assets-section">
        <h2 className="text-base font-semibold text-slate-800 mb-3">IT Assets</h2>
        {assets.length === 0 ? (
          <div className="pk-card p-8 text-center text-slate-400 text-sm">No IT assets assigned to you yet.</div>
        ) : viewMode === "list" ? (
          <div className="pk-card overflow-x-auto" data-testid="my-assets-list">
            <table className="w-full text-sm responsive-table">
              <thead className="bg-slate-50/80 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Asset</th>
                  <th className="px-4 py-3 font-semibold">Tag / Serial</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Deduction</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => {
                  const rs = a.return_status;
                  const canRequest = (!rs || rs === "rejected") && a.status === "Assigned";
                  return (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50/60" data-testid={`asset-row-${a.asset_tag}`}>
                      <td data-label="Asset" className="px-4 py-3">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{a.asset_type || "Asset"}</div>
                        <div className="font-semibold text-slate-800">{a.make_model || a.asset_tag}</div>
                      </td>
                      <td data-label="Tag / Serial" className="px-4 py-3 mono text-[11.5px] text-slate-600">
                        <div>TAG · {a.asset_tag}</div>
                        {a.serial_no && <div>S/N · {a.serial_no}</div>}
                      </td>
                      <td data-label="Status" className="px-4 py-3">
                        <Badge className={
                          a.status === "Assigned" ? "bg-green-100 text-green-700" :
                          a.status === "In Repair" ? "bg-amber-100 text-amber-800" :
                          a.status === "Damaged" ? "bg-rose-100 text-rose-800" :
                          "bg-slate-100 text-slate-700"
                        }>{a.status || "—"}</Badge>
                        {rs === "requested" && <div className="mt-1"><Badge className="bg-amber-100 text-amber-800 text-[10px]">Return Requested</Badge></div>}
                        {rs === "rejected" && <div className="mt-1"><Badge className="bg-rose-100 text-rose-800 text-[10px]" title={a.return_it_remark}>Return Rejected</Badge></div>}
                        {rs === "returned" && a.return_condition && <div className="mt-1"><Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Returned · {a.return_condition}</Badge></div>}
                      </td>
                      <td data-label="Deduction" className="px-4 py-3 mono text-[12px]">
                        {rs === "returned" && a.return_deduct_cost && Number(a.return_deduct_amount) > 0
                          ? <span className="font-semibold text-rose-700">−₹ {Number(a.return_deduct_amount).toLocaleString("en-IN")}</span>
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td data-label="Action" className="px-4 py-3 text-right">
                        {canRequest && (
                          <Button size="sm" onClick={() => { setAssetReturnTarget(a); setReturnRemark(""); }} className="bg-violet-600 hover:bg-violet-700 h-7 text-[11px]" data-testid={`asset-return-btn-${a.id}`}>
                            <Undo2 size={11} className="mr-1" /> Return
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map((a) => {
              const rs = a.return_status;
              const canRequest = (!rs || rs === "rejected") && a.status === "Assigned";
              return (
                <div key={a.id} className="pk-card p-5" data-testid={`asset-card-${a.asset_tag}`}>
                  <div className="pk-table-header mb-2">{a.asset_type || "Asset"}</div>
                  <div className="heading text-lg font-bold text-slate-900">{a.make_model || a.asset_tag}</div>
                  <div className="mono text-[11px] text-slate-500 mt-1">TAG · {a.asset_tag}</div>
                  {a.serial_no && <div className="mono text-[11px] text-slate-500">S/N · {a.serial_no}</div>}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Badge className={
                      a.status === "Assigned" ? "bg-green-100 text-green-700" :
                      a.status === "In Repair" ? "bg-amber-100 text-amber-800" :
                      a.status === "Damaged" ? "bg-rose-100 text-rose-800" :
                      "bg-slate-100 text-slate-700"
                    }>{a.status || "—"}</Badge>
                    {rs === "requested" && <Badge className="bg-amber-100 text-amber-800">Return Requested</Badge>}
                    {rs === "rejected" && <Badge className="bg-rose-100 text-rose-800" title={a.return_it_remark}>Return Rejected</Badge>}
                    {rs === "returned" && a.return_condition && (
                      <Badge className={
                        a.return_condition === "Damaged" ? "bg-rose-100 text-rose-800" :
                        a.return_condition === "Non-Working" ? "bg-amber-100 text-amber-800" :
                        "bg-emerald-100 text-emerald-800"
                      }>Returned · {a.return_condition}</Badge>
                    )}
                  </div>
                  {rs === "returned" && a.return_deduct_cost && Number(a.return_deduct_amount) > 0 && (
                    <div className="mono text-[12px] font-semibold text-rose-700 mt-2">Deduction · −₹ {Number(a.return_deduct_amount).toLocaleString("en-IN")}</div>
                  )}
                  {canRequest && (
                    <Button size="sm" onClick={() => { setAssetReturnTarget(a); setReturnRemark(""); }} className="mt-3 w-full bg-violet-600 hover:bg-violet-700 h-8 text-[12px]" data-testid={`asset-return-btn-${a.id}`}>
                      <Undo2 size={12} className="mr-1" /> Return Asset
                    </Button>
                  )}
                  {rs === "requested" && <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">Awaiting IT Team approval.</div>}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Issued Inventory */}
      <section data-testid="my-issuances-section">
        <h2 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Package size={16} className="text-violet-600" /> Materials Issued to You
        </h2>
        {issuances.length === 0 ? (
          <div className="pk-card p-8 text-center text-slate-400 text-sm">No materials issued to you yet.</div>
        ) : viewMode === "list" ? (
          <div className="pk-card overflow-x-auto" data-testid="my-issuances-list">
            <table className="w-full text-sm responsive-table">
              <thead className="bg-slate-50/80 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Issuance</th>
                  <th className="px-4 py-3 font-semibold">Items</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Deduction</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {issuances.map((i) => {
                  const meta = ISSUANCE_STATUS_META[i.status] || {};
                  const canRequestReturn = i.status === "issued";
                  const issDeduction = (i.items || []).filter((it) => it.deduct_cost).reduce((s, it) => s + Number(it.deduct_amount || 0), 0);
                  return (
                    <tr key={i.id} className="border-b last:border-0 hover:bg-slate-50/60" data-testid={`issuance-row-${i.id}`}>
                      <td data-label="Issuance" className="px-4 py-3">
                        <div className="mono text-[11.5px] font-bold text-slate-700">{i.issuance_no}</div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5">Issued {i.issued_at ? new Date(i.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                      </td>
                      <td data-label="Items" className="px-4 py-3 text-[12px]">
                        {(i.items || []).map((line) => (
                          <div key={line.line_id} className="flex items-center gap-2 mb-1 last:mb-0">
                            <span className="font-medium text-slate-800 truncate max-w-[200px]">{line.inventory_name}</span>
                            <span className="mono text-[10.5px] text-slate-500">×{line.qty_issued}</span>
                          </div>
                        ))}
                      </td>
                      <td data-label="Status" className="px-4 py-3">
                        <Badge className={meta.chip || "bg-slate-100 text-slate-700"}>{meta.label || i.status}</Badge>
                      </td>
                      <td data-label="Deduction" className="px-4 py-3 mono text-[12px]">
                        {issDeduction > 0 ? <span className="font-semibold text-rose-700">−₹ {issDeduction.toLocaleString("en-IN")}</span> : <span className="text-slate-400">—</span>}
                      </td>
                      <td data-label="Action" className="px-4 py-3 text-right">
                        {canRequestReturn && (
                          <Button size="sm" onClick={() => { setReturnTarget(i); setReturnRemark(""); }} className="bg-violet-600 hover:bg-violet-700 h-7 text-[11px]" data-testid={`issuance-return-${i.id}`}>
                            <Undo2 size={11} className="mr-1" /> Return
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {issuances.map((i) => {
              const meta = ISSUANCE_STATUS_META[i.status] || {};
              const canRequestReturn = i.status === "issued";
              const issDeduction = (i.items || []).filter((it) => it.deduct_cost).reduce((s, it) => s + Number(it.deduct_amount || 0), 0);
              return (
                <div key={i.id} className="pk-card p-5" data-testid={`issuance-card-${i.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="mono text-[11px] font-bold text-slate-700">{i.issuance_no}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Issued {i.issued_at ? new Date(i.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                    </div>
                    <div className="text-right">
                      <Badge className={`${meta.chip || "bg-slate-100 text-slate-700"}`}>{meta.label || i.status}</Badge>
                      {issDeduction > 0 && <div className="mono text-[11px] font-semibold text-rose-700 mt-1">−₹ {issDeduction.toLocaleString("en-IN")}</div>}
                    </div>
                  </div>
                  <div className="space-y-1.5 mb-2">
                    {(i.items || []).map((line) => (
                      <div key={line.line_id} className="flex items-start justify-between gap-2 text-[12px] border-b border-slate-100 last:border-0 pb-1.5 last:pb-0">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{line.inventory_name}</div>
                          {line.inventory_sku && <div className="mono text-[10px] text-slate-500">{line.inventory_sku}</div>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="mono font-semibold text-slate-700">Qty {line.qty_issued}</div>
                          {i.status === "returned" && line.return_condition && (
                            <div className="mt-1">
                              <Badge className={
                                line.return_condition === "Damaged" ? "bg-rose-100 text-rose-800 text-[10px]" :
                                line.return_condition === "Non-Working" ? "bg-amber-100 text-amber-800 text-[10px]" :
                                "bg-emerald-100 text-emerald-800 text-[10px]"
                              }>{line.return_condition}</Badge>
                              {line.deduct_cost && <div className="mono text-[10px] text-rose-700 font-semibold mt-0.5">−₹ {Number(line.deduct_amount).toLocaleString("en-IN")}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {i.status === "return_requested" && <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">Awaiting team approval.</div>}
                  {canRequestReturn && (
                    <Button size="sm" onClick={() => { setReturnTarget(i); setReturnRemark(""); }} className="mt-2 w-full bg-violet-600 hover:bg-violet-700 h-8 text-[12px]" data-testid={`issuance-return-${i.id}`}>
                      <Undo2 size={12} className="mr-1" /> Return Material
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* My Requests — shown at the end so day-to-day issued items stay top */}
      {(assetRequests.length > 0 || issuanceRequests.length > 0) && (
        <section data-testid="my-requests-section">
          <h2 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Inbox size={16} className="text-violet-600" /> My Requests
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {assetRequests.length > 0 && (
              <div className="pk-card p-4">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">IT Asset Requests</div>
                <ul className="space-y-2">
                  {assetRequests.map((r) => {
                    const m = REQ_STATUS_META[r.status] || { label: r.status };
                    return (
                      <li key={r.id} className="border border-slate-100 rounded p-2.5 text-[12px]" data-testid={`my-asset-req-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-slate-800">{r.product_name}</div>
                          <Badge className={m.chip || "bg-slate-100"}>{m.label}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10.5px] text-slate-500">
                          <Badge className={PRIORITY_CHIP[r.priority]}>{r.priority}</Badge>
                          {r.required_by && <span className="mono">By {r.required_by}</span>}
                          {r.cost > 0 && <span className="mono">₹ {Number(r.cost).toLocaleString("en-IN")}</span>}
                          {r.approved_asset_tag && <span className="mono text-emerald-700">→ {r.approved_asset_tag}</span>}
                        </div>
                        {r.remark && <div className="text-slate-500 italic mt-1">{r.remark}</div>}
                        {r.status === "rejected" && r.decision_remark && <div className="text-rose-600 mt-1">IT: {r.decision_remark}</div>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {issuanceRequests.length > 0 && (
              <div className="pk-card p-4">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Material / Product Requests</div>
                <ul className="space-y-2">
                  {issuanceRequests.map((r) => {
                    const m = REQ_STATUS_META[r.status] || { label: r.status };
                    return (
                      <li key={r.id} className="border border-slate-100 rounded p-2.5 text-[12px]" data-testid={`my-iss-req-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-slate-800">{r.project_name || "Material Request"}</div>
                          <Badge className={m.chip || "bg-slate-100"}>{m.label}</Badge>
                        </div>
                        <ul className="mt-1 ml-3 list-disc text-slate-700">
                          {(r.items || []).map((it, i) => (
                            <li key={i}>{it.inventory_name} <span className="mono text-slate-500">× {it.qty}</span></li>
                          ))}
                        </ul>
                        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10.5px] text-slate-500">
                          <Badge className={PRIORITY_CHIP[r.priority]}>{r.priority}</Badge>
                          {r.required_by && <span className="mono">By {r.required_by}</span>}
                          {r.issued_issuance_no && <span className="mono text-emerald-700">→ {r.issued_issuance_no}</span>}
                        </div>
                        {r.reason && <div className="text-slate-500 italic mt-1">{r.reason}</div>}
                        {r.status === "rejected" && r.decision_remark && <div className="text-rose-600 mt-1">{r.decision_remark}</div>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Material Return Dialog */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) { setReturnTarget(null); setReturnRemark(""); } }}>
        <DialogContent className="max-w-md">
          {returnTarget && (
            <>
              <DialogHeader><DialogTitle>Return Material</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="p-3 bg-slate-50 rounded border text-[12px]">
                  <div className="text-slate-600 font-semibold mb-1">{returnTarget.issuance_no}</div>
                  {(returnTarget.items || []).map((line) => (
                    <div key={line.line_id} className="flex justify-between text-slate-700"><span className="truncate">{line.inventory_name}</span><span className="mono font-semibold">Qty {line.qty_issued}</span></div>
                  ))}
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Notes</Label>
                  <Textarea value={returnRemark} onChange={(e) => setReturnRemark(e.target.value)} rows={3} className="mt-1.5" data-testid="return-remark" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReturnTarget(null); setReturnRemark(""); }} disabled={saving}>Cancel</Button>
                <Button onClick={submitReturn} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="return-submit">{saving ? "Submitting…" : "Submit Return Request"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Return Dialog */}
      <Dialog open={!!assetReturnTarget} onOpenChange={(o) => { if (!o) { setAssetReturnTarget(null); setReturnRemark(""); } }}>
        <DialogContent className="max-w-md">
          {assetReturnTarget && (
            <>
              <DialogHeader><DialogTitle>Return IT Asset</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="p-3 bg-slate-50 rounded border text-[12px]">
                  <div className="text-slate-600 font-semibold">{assetReturnTarget.make_model || assetReturnTarget.asset_tag}</div>
                  <div className="mono text-[11px] text-slate-500">TAG · {assetReturnTarget.asset_tag}</div>
                  {assetReturnTarget.serial_no && <div className="mono text-[11px] text-slate-500">S/N · {assetReturnTarget.serial_no}</div>}
                  <div className="text-slate-500 mt-2">IT Team will inspect &amp; mark condition. Damaged/Non-Working may incur a cost deduction.</div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Condition notes (optional)</Label>
                  <Textarea value={returnRemark} onChange={(e) => setReturnRemark(e.target.value)} rows={3} className="mt-1.5" data-testid="asset-return-remark" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAssetReturnTarget(null); setReturnRemark(""); }} disabled={saving}>Cancel</Button>
                <Button onClick={submitAssetReturn} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="asset-return-submit">{saving ? "Submitting…" : "Submit Return Request"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Raise Request Dialog */}
      <Dialog open={requestOpen} onOpenChange={(o) => { setRequestOpen(o); if (!o) { setRequestType(""); resetForms(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Raise a Request</DialogTitle></DialogHeader>

          {!requestType && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="req-type-picker">
              <button onClick={() => setRequestType("it_asset")} className="text-left p-4 border-2 border-slate-200 rounded-lg hover:border-violet-400 hover:bg-violet-50/40 transition" data-testid="req-type-it-asset">
                <div className="font-bold text-slate-800">IT Asset</div>
                <div className="text-[12px] text-slate-500 mt-0.5">Laptop, monitor, phone, etc. — assigned by IT.</div>
              </button>
              <button onClick={() => setRequestType("product")} className="text-left p-4 border-2 border-slate-200 rounded-lg hover:border-violet-400 hover:bg-violet-50/40 transition" data-testid="req-type-product">
                <div className="font-bold text-slate-800">Product / Material</div>
                <div className="text-[12px] text-slate-500 mt-0.5">Items from inventory — issued by Dispatch.</div>
              </button>
            </div>
          )}

          {requestType === "it_asset" && (
            <div className="space-y-3 py-2" data-testid="req-form-it-asset">
              <div>
                <Label>Product Name *</Label>
                <Input value={assetReqForm.product_name} onChange={(e) => setAssetReqForm({ ...assetReqForm, product_name: e.target.value })} placeholder="e.g. Dell Latitude 5440" className="mt-1.5" data-testid="req-asset-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Cost of Product (₹)</Label>
                  <Input type="number" min="0" value={assetReqForm.cost} onChange={(e) => setAssetReqForm({ ...assetReqForm, cost: e.target.value })} className="mt-1.5 mono" data-testid="req-asset-cost" />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={assetReqForm.priority} onValueChange={(v) => setAssetReqForm({ ...assetReqForm, priority: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="req-asset-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date Required By</Label>
                <Input type="date" value={assetReqForm.required_by} onChange={(e) => setAssetReqForm({ ...assetReqForm, required_by: e.target.value })} className="mt-1.5" data-testid="req-asset-date" />
              </div>
              <div>
                <Label>Why is this product required? *</Label>
                <Textarea rows={3} value={assetReqForm.remark} onChange={(e) => setAssetReqForm({ ...assetReqForm, remark: e.target.value })} className="mt-1.5" placeholder="Brief reason / business justification…" data-testid="req-asset-remark" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRequestType("")}>Back</Button>
                <Button onClick={submitAssetRequest} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="req-asset-submit">{saving ? "Submitting…" : "Submit IT Asset Request"}</Button>
              </DialogFooter>
            </div>
          )}

          {requestType === "product" && (
            <div className="space-y-3 py-2" data-testid="req-form-product">
              <div>
                <Label>Products & Quantity *</Label>
                <div className="space-y-2 mt-1.5">
                  {issReqForm.items.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-8">
                        <Select value={line.inventory_id} onValueChange={(v) => {
                          const items = [...issReqForm.items]; items[idx] = { ...items[idx], inventory_id: v }; setIssReqForm({ ...issReqForm, items });
                        }}>
                          <SelectTrigger data-testid={`req-prod-item-${idx}`}><SelectValue placeholder="Pick product…" /></SelectTrigger>
                          <SelectContent>
                            {inventory.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No inventory available</div>}
                            {inventory.map((inv) => <SelectItem key={inv.id} value={inv.id}>{inv.product_name || inv.name} ({inv.sku || "—"})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input type="number" min="1" value={line.qty} onChange={(e) => {
                          const items = [...issReqForm.items]; items[idx] = { ...items[idx], qty: e.target.value }; setIssReqForm({ ...issReqForm, items });
                        }} className="mono" placeholder="Qty" data-testid={`req-prod-qty-${idx}`} />
                      </div>
                      <div className="col-span-1">
                        <Button size="icon" variant="ghost" onClick={() => {
                          const items = issReqForm.items.filter((_, i) => i !== idx); setIssReqForm({ ...issReqForm, items: items.length ? items : [{ inventory_id: "", qty: 1 }] });
                        }} disabled={issReqForm.items.length === 1} className="h-9 w-9">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setIssReqForm({ ...issReqForm, items: [...issReqForm.items, { inventory_id: "", qty: 1 }] })} data-testid="req-prod-add-line">
                    <Plus size={12} className="mr-1" /> Add another product
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Project Name</Label>
                  <Input value={issReqForm.project_name} onChange={(e) => setIssReqForm({ ...issReqForm, project_name: e.target.value })} className="mt-1.5" placeholder="e.g. ABC School Setup" data-testid="req-prod-project" />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={issReqForm.priority} onValueChange={(v) => setIssReqForm({ ...issReqForm, priority: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="req-prod-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date Required Till</Label>
                <Input type="date" value={issReqForm.required_by} onChange={(e) => setIssReqForm({ ...issReqForm, required_by: e.target.value })} className="mt-1.5" data-testid="req-prod-date" />
              </div>
              <div>
                <Label>Why is the product required? *</Label>
                <Textarea rows={3} value={issReqForm.reason} onChange={(e) => setIssReqForm({ ...issReqForm, reason: e.target.value })} className="mt-1.5" placeholder="Brief justification…" data-testid="req-prod-reason" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRequestType("")}>Back</Button>
                <Button onClick={submitIssuanceRequest} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="req-prod-submit">{saving ? "Submitting…" : "Submit Material Request"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
